import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHeaderLight from "../components/shared/PageHeaderLight";
import { useStateValue } from "../StateProvider";
import { getDefaultValue, getUser, logout } from "../utils";
import { deleteAccount, loadProfile } from "../utils/Network";
import "./Dashboard.css";
import "./Signin.css";

/**
 * Standalone account deletion page at /delete-account.
 * Requires sign-in. Confirm with password, or by typing the account email
 * (for Google/Apple accounts without a password).
 */
export default function DeleteAccount() {
  const [{ translations }] = useStateValue();
  const translate = (str) => {
    if (!translations) return str;
    const t = translations.find((x) => x.key == str);
    if (t) return getDefaultValue(t);
    return str;
  };

  const sessionUser = getUser();
  const [profile, setProfile] = useState(null);
  const [confirmMode, setConfirmMode] = useState("password");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    loadProfile().then((p) => {
      if (p) setProfile(p);
    });
  }, []);

  const accountEmail =
    (profile && profile.email) ||
    (sessionUser && (sessionUser.email || sessionUser.username)) ||
    "";

  const handleDelete = (e) => {
    e.preventDefault();
    setError(false);

    if (confirmMode === "password") {
      if (!password) {
        setError(translate("Please enter your password"));
        return;
      }
    } else if (!email.trim()) {
      setError(translate("Please enter your email"));
      return;
    } else if (
      accountEmail &&
      email.trim().toLowerCase() !== String(accountEmail).trim().toLowerCase()
    ) {
      setError(translate("Email does not match this account."));
      return;
    }

    if (
      !window.confirm(
        translate(
          "This will permanently delete your account and data. Continue?"
        )
      )
    ) {
      return;
    }

    setLoading(true);
    const payload =
      confirmMode === "password"
        ? { password }
        : { email: email.trim() };

    deleteAccount(payload).then((res) => {
      setLoading(false);
      if (res.ok) {
        setDone(true);
        logout();
        return;
      }
      const detail =
        res.data &&
        (res.data.detail ||
          res.data.error ||
          (res.data.non_field_errors && res.data.non_field_errors[0]));
      setError(detail || translate("Something went wrong"));
    });
  };

  if (done) {
    return (
      <div className="participant-dashboard">
        <div role="main" className="main page-background">
          <PageHeaderLight />
          <div className="container participant-dashboard__container pt-3 pb-5">
            <div className="row justify-content-center">
              <div className="col-lg-7 col-md-9">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4 text-center">
                    <h1 className="text-5 font-weight-bold mb-3">
                      {translate("Account deleted")}
                    </h1>
                    <p className="text-3 text-color-default mb-4">
                      {translate(
                        "Your account and related data have been permanently removed."
                      )}
                    </p>
                    <Link to="/" className="btn rounded-35 btn-rash btn-modern">
                      {translate("Back to home")}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="participant-dashboard">
      <div role="main" className="main page-background">
        <PageHeaderLight />
        <div className="container participant-dashboard__container pt-3 pb-5">
          <div className="row justify-content-center">
            <div className="col-lg-7 col-md-9">
              <div className="card border-0 shadow-sm participant-account-tab__card participant-account-tab__card--danger">
                <div className="card-body p-4">
                  <h1 className="text-5 font-weight-bold mb-2 text-danger">
                    {translate("Delete account permanently")}
                  </h1>
                  <p className="text-3 text-color-default mb-3">
                    {translate(
                      "This removes your user account and related participation data. This action cannot be undone."
                    )}
                  </p>
                  {accountEmail ? (
                    <p className="text-3 mb-4">
                      {translate("Signed in as")}{" "}
                      <strong>{accountEmail}</strong>
                    </p>
                  ) : null}

                  <div className="mb-3">
                    <div className="custom-control custom-radio mb-2">
                      <input
                        type="radio"
                        id="delete-confirm-password"
                        name="delete-confirm-mode"
                        className="custom-control-input"
                        checked={confirmMode === "password"}
                        onChange={() => {
                          setConfirmMode("password");
                          setError(false);
                        }}
                      />
                      <label
                        className="custom-control-label"
                        htmlFor="delete-confirm-password"
                      >
                        {translate("Confirm with password")}
                      </label>
                    </div>
                    <div className="custom-control custom-radio">
                      <input
                        type="radio"
                        id="delete-confirm-email"
                        name="delete-confirm-mode"
                        className="custom-control-input"
                        checked={confirmMode === "email"}
                        onChange={() => {
                          setConfirmMode("email");
                          setError(false);
                        }}
                      />
                      <label
                        className="custom-control-label"
                        htmlFor="delete-confirm-email"
                      >
                        {translate("Confirm with account email")}
                      </label>
                    </div>
                  </div>

                  <form onSubmit={handleDelete}>
                    {confirmMode === "password" ? (
                      <div className="form-group mb-3">
                        <input
                          className="form-control text-3 h-auto py-2 rounded-35"
                          type="password"
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder={translate("Current password")}
                        />
                        <small className="form-text text-muted">
                          {translate(
                            "Google or Apple accounts without a password should use email confirmation instead."
                          )}
                        </small>
                      </div>
                    ) : (
                      <div className="form-group mb-3">
                        <input
                          className="form-control text-3 h-auto py-2 rounded-35"
                          type="email"
                          autoComplete="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder={translate("Type your account email")}
                        />
                      </div>
                    )}

                    <button
                      type="submit"
                      className="btn rounded-35 btn-danger btn-modern"
                      disabled={loading}
                    >
                      {translate("Delete my account permanently")}
                    </button>
                    <Link
                      to="/profile"
                      className="btn rounded-35 btn-outline-secondary btn-modern ml-2"
                    >
                      {translate("Cancel")}
                    </Link>
                  </form>

                  {loading && (
                    <div className="alert alert-info mt-3 mb-0">
                      {translate("Please Wait...")}
                    </div>
                  )}
                  {error && (
                    <div className="alert alert-danger mt-3 mb-0">{error}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
